import { AUTH_STORAGE_KEY, AuthTokenInterceptor } from '@foundation/auth/state';
import { OfflineInterceptor } from '@foundation/network/interceptors';
import { CacheInterceptor } from '@foundation/utils';
import { Dialog, DialogModule } from '@angular/cdk/dialog';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter, Router, withComponentInputBinding, withEnabledBlockingInitialNavigation, withRouterConfig } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes.config';
import { ChunkLoadErrorHandler } from './chunk-load-error-handler';

export const appConfig: ApplicationConfig = {
	providers: [
		{ provide: AUTH_STORAGE_KEY, useValue: 'auth-spoken' },
		provideZonelessChangeDetection(),
		provideRouter(
			//
			appRoutes,
			withEnabledBlockingInitialNavigation(),
			withComponentInputBinding(),
			withRouterConfig({
				paramsInheritanceStrategy: 'always',
			})
		),
		provideHttpClient(
			withInterceptors([
				AuthTokenInterceptor,
				CacheInterceptor,
				...(environment.production ? [OfflineInterceptor] : []), // Ensure this is imported from the correct path
				// RedirectInterceptor
			])
		),
		Dialog, // used in define path modal
		importProvidersFrom(DialogModule),
		importProvidersFrom(BrowserAnimationsModule),
	],
};

if (environment.production) {
	// Stale-chunk reload recovery runs on every production origin (incl. custom
	// domains). Sentry is only wired on its configured domain.
	const sentryDomain = environment.sentry.domain;
	const currentHostname = window.location.hostname;
	// Exact host or a subdomain of it — not a substring match, so
	// e.g. spoken.systems.evil.com does not enable Sentry.
	const sentryEnabled = typeof sentryDomain === 'string' && (currentHostname === sentryDomain || currentHostname.endsWith(`.${sentryDomain}`));
	appConfig.providers.push({
		provide: ErrorHandler,
		useValue: new ChunkLoadErrorHandler(sentryEnabled ? Sentry.createErrorHandler() : new ErrorHandler()),
	});
	if (sentryEnabled) {
		appConfig.providers.push(
			{
				provide: Sentry.TraceService,
				deps: [Router],
			},
			provideAppInitializer(() => {
				inject(Sentry.TraceService);
			})
		);
	}
}
